import { useState, useCallback } from 'react';
import { useApolloClient, useMutation, useQuery } from 'react-apollo';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { albumAddPhotos as albumAddPhotosMutation } from '../../../../graphql/mutations/albums';
import { autoContributorJoin as autoContributorJoinMutation } from '../../../../graphql/mutations/contributors';
import { MeQuery } from '../../../../graphql/queries/account';
import { photosUpload as photosUploadMutation } from '../../../../apps/consumer/graphql/mutations/photos';
import {
  VideoDelete as VideoDeleteMutation,
  VideosUpload as videosUploadMutation,
} from '../../../../graphql/mutations/video';
import {
  albumFileUpload,
  albumUploadReset as albumUploadResetAction,
} from '../../../../actions/album-upload';
import useOpenAlert from '../../../../hooks/useOpenAlert';
import { getErrorString } from '../../../../util/normalize-errors';
import {
  isMainPhotosAlbum,
  isMemoryBook,
  isSpecialtyAlbum,
  isVGBContext,
  isVideoAlbum,
} from '../../../../util/album';
import { HierarchyViewsCreate } from '../../../../graphql/mutations/hierarchy-groups';
import { isAlbumOwner } from '../../../../util/account';
import { VIDEO_TYPE } from '../../../../constants/videos';
import { AlbumForContributors } from '../../../../graphql/queries/contributors';
import { isVideo } from '../../../../apps/pro/components/UploadProgressTracking/helpers';
import { AlbumFolderForSpecialtyAlbumInvites } from '../../../../graphql/queries/album-folder';
import { uploadBlockedByStorageSizeLimit } from '../../../../apps/pro/views/AlbumFolder/AlbumFolderUpload/helpers';

const useSpecialtyEventUpload = ({ authenticated, albumFolderId }) => {
  const openAlert = useOpenAlert();
  const { data: { me: user } = {} } = useQuery(MeQuery, {
    skip: !authenticated,
  });
  const { total, current, error, success, uploading } = useSelector(
    ({ albumUploads }) => albumUploads,
  );
  const dispatch = useDispatch();
  const client = useApolloClient();
  const history = useHistory();
  const [hasUploadedWaldofied, setHasUploadedWaldofied] = useState(false);
  const [hasUploadedMemoryBook, setHasUploadedMemoryBook] = useState(false);
  const [hasUploadedGroupVideo, setHasUploadedGroupVideo] = useState(false);
  const [uploadedTargets, setUploadedTargets] = useState([]);

  // Upload
  const [photosUpload] = useMutation(photosUploadMutation);
  const [videosUpload] = useMutation(videosUploadMutation);
  const [autoContributorJoin] = useMutation(autoContributorJoinMutation);
  const [hierarchyViewsCreate] = useMutation(HierarchyViewsCreate);
  const [albumAddPhotos] = useMutation(albumAddPhotosMutation);
  const albumUploadReset = useCallback(
    () => dispatch(albumUploadResetAction()),
    [dispatch],
  );

  // Delete Videos
  const [videoDelete] = useMutation(VideoDeleteMutation);

  const onPhotosUpload = async (accountId, files) => {
    const result = await photosUpload({
      variables: {
        input: {
          account_id: accountId,
          files,
          is_external_upload: true,
        },
      },
    });

    return result;
  };

  const onVideosUpload = async videos => {
    const result = await videosUpload({
      variables: {
        input: {
          videos,
          is_external_upload: true,
        },
      },
    });

    return result;
  };

  const onAlbumAddPhotos = async (albumId, photoIds) => {
    await albumAddPhotos({
      variables: {
        input: {
          album_id: albumId,
          photo_ids: photoIds,
          is_external_upload: true,
        },
      },
    });
  };

  const onAutoContributorJoin = async entityId => {
    await autoContributorJoin({
      variables: {
        input: {
          id: entityId,
        },
      },
    });
  };

  const onHierarchyViewsCreate = async entityIds => {
    if (!entityIds || entityIds.length === 0) {
      return;
    }
    await hierarchyViewsCreate({
      variables: {
        input: {
          hierarchy_item_ids: entityIds,
          platform: 'WEB',
        },
      },
    });
  };

  const onUpload = async (photosOrVideos, album) => {
    const {
      data: { albumFolder: albumFolderForStorageCheck },
    } = await client.query({
      query: AlbumFolderForSpecialtyAlbumInvites,
      variables: {
        id: albumFolderId,
      },
    });
    const uploadSize = photosOrVideos.reduce(
      (sum, file) => sum + (file.size ?? 0),
      0,
    );

    if (
      uploadBlockedByStorageSizeLimit({
        albumFolder: albumFolderForStorageCheck,
        account: albumFolderForStorageCheck?.account,
        uploadSize,
      })
    ) {
      openAlert({
        message: 'There was a problem while attempting to upload.',
        info: 'Storage limit exceeded, please ask the owner of the event to visit their Subscription tab and increase their storage limit.',
      });

      return;
    }

    const videos = photosOrVideos.filter(file => isVideo(file));

    try {
      // Safety check - if the user has the ability to upload to a specialty album,
      // said album may have a different setup than the albumFolder may suggest. Auto
      // contributor joining will avoid the issue where a NONE albumFolder auto join
      // overrides a default auto contributor setting for a specialty album allowing
      // uploads.
      if (user?.id && isSpecialtyAlbum(album)) {
        await onAutoContributorJoin(album.id);
      }

      await albumFileUpload(
        user?.id,
        album.id,
        photosOrVideos,
        dispatch,
        onPhotosUpload,
        onVideosUpload,
        onAlbumAddPhotos,
        undefined,
        undefined,
        {
          type: isAlbumOwner(user, album) ? VIDEO_TYPE.GREETING : undefined,
        },
      );
      if (videos.length > 0) {
        // We should delete existent videos if the album requires it
        if (isVGBContext(album) && !!user) {
          try {
            const {
              data: { album: albumWithUserVideos },
            } = await client.query({
              query: AlbumForContributors,
              variables: {
                id: album.id,
                include_user_uploaded_videos: true,
                user_uploaded_videos: user.id,
              },
            });

            if (albumWithUserVideos?.user_uploaded_videos?.total > 1) {
              const olderVideo =
                albumWithUserVideos?.user_uploaded_videos?.records?.[0];

              await videoDelete({
                variables: {
                  input: {
                    video_id: olderVideo.id,
                  },
                },
              });
            }
          } catch {
            //  We'll do nothing in this case. Worst case scenario user ends with 2 videos.
          }
        }
      }
    } catch (e) {
      openAlert(getErrorString(e));
    }
  };

  const onUpdateUploadedStates = album => {
    setUploadedTargets([...uploadedTargets, album]);

    if (isMainPhotosAlbum(album)) {
      setHasUploadedWaldofied(true);
      if (authenticated && album?.root_album_folder) {
        history.push(`/app/shutterbug/folders/${album?.root_album_folder?.id}`);
      } else if (album?.root_album_folder) {
        history.push(`/galleries/view/folder/${album?.root_album_folder?.id}`);
      }
    } else if (!isMemoryBook(album) && !isVideoAlbum(album)) {
      if (authenticated && album?.root_album_folder) {
        history.push(`/app/shutterbug/folders/${album?.root_album_folder?.id}`);
      } else if (album?.root_album_folder) {
        history.push(`/galleries/view/folder/${album?.root_album_folder?.id}`);
      }
    }

    if (isMemoryBook(album)) {
      setHasUploadedMemoryBook(true);
    }

    if (isVideoAlbum(album)) {
      setHasUploadedGroupVideo(true);
    }
  };

  return {
    hasUploadedWaldofied,
    hasUploadedMemoryBook,
    hasUploadedGroupVideo,
    total,
    current,
    error,
    success,
    uploading,
    albumUploadReset,
    onAutoContributorJoin,
    onHierarchyViewsCreate,
    onUpdateUploadedStates,
    onUpload,
    uploadedTargets,
    user,
  };
};

export default useSpecialtyEventUpload;
